import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
} from '@mui/icons-material';

// ─── Password strength helpers ─────────────────────────────────────────────────
const getStrength = (pwd) => {
  if (!pwd) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pwd.length >= 8)           score++;
  if (/[A-Z]/.test(pwd))         score++;
  if (/[0-9]/.test(pwd))         score++;
  if (/[^A-Za-z0-9]/.test(pwd))  score++;
  const map = [
    { label: '',       color: 'transparent' },
    { label: 'Weak',   color: '#ef4444' },
    { label: 'Fair',   color: '#f59e0b' },
    { label: 'Good',   color: '#3b82f6' },
    { label: 'Strong', color: '#10b981' },
  ];
  return { score, ...map[score] };
};

// ─── Shared dark TextField style (same as Login) ───────────────────────────────
const fieldSx = (mb = 2.5) => ({
  mb,
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
  },
  '& .MuiInputLabel-root': { color: '#71717a' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#a855f7' },
  '& input': { color: '#fafafa' },
  '& .MuiSelect-select': { color: '#fafafa' },
  '& .MuiIconButton-root': { color: '#71717a' },
});

// ─── Register Component ─────────────────────────────────────────────────────────
const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'student',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { loginWithUserData } = useAuth();
  const navigate              = useNavigate();
  const strength              = getStrength(formData.password);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError(t('auth.register.errorTerms')); return; }
    if (formData.password !== formData.confirmPassword) { setError(t('auth.register.errorPasswordMatch')); return; }
    if (strength.score < 2) { setError(t('auth.register.errorWeakPassword')); return; }

    setLoading(true);
    setError('');
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          password: formData.password,
          role: formData.role,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.pendingApproval || data.data?.user?.status === 'pending') {
          setError('');
          navigate('/login', { state: { notice: data.message || 'Your instructor application is awaiting admin approval.' } });
          return;
        }
        const userData = data.data?.user || data.user;
        const token    = data.data?.token || data.token;
        if (userData && token) {
          loginWithUserData(userData, token);
          navigate(userData.role === 'instructor' ? '/creator-dashboard' : '/studentportal');
        } else {
          setError(t('auth.register.errorInvalidResponse'));
        }
      } else {
        setError(data.message || t('auth.register.errorGeneric'));
      }
    } catch {
      setError(t('auth.register.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#09090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/images/logo/logo-qorenova.png"
            alt="QoreNova"
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{ width: 48, height: 48, borderRadius: 2, mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#fafafa', letterSpacing: '-0.02em' }}>
            {t('auth.register.appName')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#71717a', mt: 0.5 }}>
            {t('auth.register.subtitle')}
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: 'rgba(239,68,68,0.1)',
                color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 2,
                '& .MuiAlert-icon': { color: '#ef4444' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>

            {/* First + Last name */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label={t('auth.register.firstNameLabel')}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
                required
                sx={fieldSx(2.5)}
              />
              <TextField
                fullWidth
                label={t('auth.register.lastNameLabel')}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
                required
                sx={fieldSx(2.5)}
              />
            </Box>

            {/* Email */}
            <TextField
              fullWidth
              label={t('auth.register.emailLabel')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
              sx={fieldSx()}
            />

            {/* Role */}
            <TextField
              select
              fullWidth
              label={t('auth.register.roleLabel')}
              name="role"
              value={formData.role}
              onChange={handleChange}
              sx={{
                ...fieldSx(),
                '& .MuiSelect-icon': { color: '#71717a' },
              }}
            >
              <MenuItem value="student">{t('auth.register.roleStudent')}</MenuItem>
              <MenuItem value="instructor">{t('auth.register.roleInstructor')}</MenuItem>
            </TextField>

            {/* Password */}
            <TextField
              fullWidth
              label={t('auth.register.passwordLabel')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              sx={fieldSx(1)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end"
                      aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Strength bar */}
            {formData.password && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Box key={i} sx={{
                      flex: 1, height: 3, borderRadius: 2,
                      bgcolor: i <= strength.score ? strength.color : 'rgba(255,255,255,0.08)',
                      transition: 'background-color 0.3s',
                    }} />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600 }}>
                  {strength.label}
                </Typography>
              </Box>
            )}
            {!formData.password && <Box sx={{ mb: 2.5 }} />}

            {/* Confirm password */}
            <TextField
              fullWidth
              label={t('auth.register.confirmPasswordLabel')}
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              required
              sx={fieldSx()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end"
                      aria-label={showConfirm ? t('auth.login.hidePassword') : t('auth.login.showPassword')}>
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Terms */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  size="small"
                  sx={{ color: '#52525b', '&.Mui-checked': { color: '#7c3aed' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  {t('auth.register.termsAgree')}{' '}
                  <Typography component="span" variant="body2"
                    sx={{ color: '#a855f7', cursor: 'pointer', fontWeight: 500 }}>
                    {t('auth.register.termsLink')}
                  </Typography>{' '}
                  &amp;{' '}
                  <Typography component="span" variant="body2"
                    sx={{ color: '#a855f7', cursor: 'pointer', fontWeight: 500 }}>
                    {t('auth.register.privacyLink')}
                  </Typography>
                </Typography>
              }
              sx={{ mb: 3, alignItems: 'flex-start' }}
            />

            {/* Submit */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '0.95rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                  boxShadow: '0 6px 28px rgba(124,58,237,0.45)',
                },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.register.createAccountBtn')}
            </Button>

            <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(255,255,255,0.08)' } }}>
              <Typography variant="caption" sx={{ color: '#52525b', px: 1 }}>{t('auth.login.orDivider')}</Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GoogleIcon sx={{ fontSize: 20 }} />}
              disabled
              sx={{
                py: 1.4,
                mb: 3,
                borderColor: 'rgba(255,255,255,0.1)',
                color: '#71717a',
                fontWeight: 500,
                borderRadius: 2,
                '&:disabled': { borderColor: 'rgba(255,255,255,0.06)', color: '#52525b' },
              }}
            >
              {t('auth.login.googleBtn')} &nbsp;
              <Typography component="span" variant="caption" sx={{ color: '#52525b' }}>{t('auth.login.comingSoon')}</Typography>
            </Button>

            <Typography variant="body2" align="center" sx={{ color: '#71717a' }}>
              {t('auth.register.alreadyHaveAccount')}{' '}
              <Typography
                component={Link}
                to="/login"
                variant="body2"
                sx={{ color: '#a855f7', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#c084fc' } }}
              >
                {t('auth.register.signInLink')}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Footer note */}
        <Typography variant="caption" align="center" display="block" sx={{ color: '#3f3f46', mt: 4 }}>
          {t('auth.login.footerNote')}
        </Typography>
      </Box>
    </Box>
  );
};

export default Register;
